import React from 'react';
import type { ProfileChanges } from '../api/studentService';

export type User = {
  email: string;
  /** The account's own name; absent for logins that don't carry one, which fall back to a name made from the email. */
  name?: string;
  firstName?: string;
  lastName?: string;
  bio?: string | null;
  role: 'mfox' | 'student' | 'college-admin' | 'mentor';
  collegeId: string | null;
  studentId: string | null;
  /** Mentor accounts only: the mentor record this login belongs to. */
  mentorId?: string | null;
  skills?: string[];
  aspiration?: string | null;
  onboardingComplete?: boolean;
};

export type UserContextType = {
  currentUser: User | null;
  isLoading: boolean;
  /** Keycloak sign-in only: why a correct sign-in was still refused (account deactivated, no role). */
  accessError?: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthorized: (requiredRoles: string | string[]) => boolean;
  completeOnboarding: (skills: string[], aspiration: string) => Promise<void>;
  /** Students only: saves profile changes and updates the signed-in user everywhere. Rejects with the server's message. */
  updateProfile: (changes: ProfileChanges) => Promise<void>;
  /** First sign-in for a student already on a college roster; `code` comes from the emailed set-up link. */
  activateAccount: (code: string, password: string, phoneNumber: string) => Promise<void>;
};

export const UserContext = React.createContext<UserContextType | null>(null);

export function useUser(): UserContextType {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
